import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowRight, Search, Trophy, TrendingUp, BookOpen, FileText,
  Pencil, Trash2, X, Download, Users, Save, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const AdminPointsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('points'); // 'points' | 'achievements'

  useEffect(() => {
    if (!user?.is_admin) { navigate('/admin'); }
  }, [user, navigate]);

  return (
    <div className="space-y-6" data-testid="admin-points-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate('/admin')}><ArrowRight className="w-5 h-5" /></Button>
        <div>
          <h1 className="text-4xl font-bold text-accent">النقاط والإنجازات</h1>
          <p className="text-muted-foreground text-lg mt-1">إدارة عرض النقاط للمستخدمين ومتابعة إنجازاتهم</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('points')}
          className={`px-4 py-2 font-semibold transition-colors ${tab === 'points' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-accent'}`}
          data-testid="tab-points"
        >
          <Trophy className="w-4 h-4 inline ml-1" /> النقاط
        </button>
        <button
          onClick={() => setTab('achievements')}
          className={`px-4 py-2 font-semibold transition-colors ${tab === 'achievements' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-accent'}`}
          data-testid="tab-achievements"
        >
          <FileText className="w-4 h-4 inline ml-1" /> الإنجازات
        </button>
      </div>

      {tab === 'points' ? <PointsSection /> : <AchievementsSection />}
    </div>
  );
};


// ============================================================================
// POINTS TAB — Global display range for user-facing counter + admin preview
// ============================================================================
const PointsSection = () => {
  const [rangeForm, setRangeForm] = useState({ start_date: '', end_date: '' });
  const [saved, setSaved] = useState({ start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Admin preview (uses their own dates)
  const [previewNum, setPreviewNum] = useState('');
  const [previewStart, setPreviewStart] = useState('');
  const [previewEnd, setPreviewEnd] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/points-display-range');
        setSaved(res.data);
        setRangeForm(res.data);
      } catch { toast.error('فشل تحميل نطاق العرض'); }
      finally { setLoading(false); }
    })();
  }, []);

  const saveRange = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/points-display-range', rangeForm);
      setSaved(res.data);
      toast.success('تم حفظ نطاق العرض العام');
    } catch { toast.error('فشل الحفظ'); }
    finally { setSaving(false); }
  };

  const clearRange = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/points-display-range', { start_date: '', end_date: '' });
      setSaved(res.data);
      setRangeForm({ start_date: '', end_date: '' });
      toast.success('تم مسح نطاق العرض — المستخدمون يرون النقاط الكلية');
    } catch { toast.error('فشل المسح'); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!previewNum.trim()) { toast.error('أدخل رقم القارئ للمعاينة'); return; }
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (previewStart) params.set('start_date', previewStart);
      if (previewEnd) params.set('end_date', previewEnd);
      const res = await api.get(`/admin/points/${previewNum.trim()}?${params}`);
      setPreviewData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل التحميل'); setPreviewData(null); }
    finally { setPreviewLoading(false); }
  };

  if (loading) return <div className="text-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div></div>;

  const hasSaved = saved.start_date || saved.end_date;

  return (
    <div className="space-y-6" data-testid="points-section">
      {/* Global display range */}
      <Card className="p-6 border-2 border-primary/20 bg-primary/5">
        <div className="flex items-start gap-3 mb-4">
          <Calendar className="w-6 h-6 text-primary mt-1" />
          <div className="flex-1">
            <h3 className="text-xl font-bold text-accent">نطاق عرض النقاط للمستخدمين</h3>
            <p className="text-sm text-muted-foreground mt-1">
              حدّد نطاقاً زمنياً ليرى المستخدمون في عدّاد نقاطهم فقط تقارير هذا النطاق (للتكريم مثلاً).
              اترك الحقول فارغة ليروا نقاطهم الكلية.
            </p>
            {hasSaved && (
              <p className="text-xs mt-2 text-primary font-semibold">
                🔒 النطاق الحالي: من <span dir="ltr">{saved.start_date || '—'}</span> إلى <span dir="ltr">{saved.end_date || '—'}</span>
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">من تاريخ</label>
            <Input type="date" value={rangeForm.start_date || ''} onChange={(e) => setRangeForm({ ...rangeForm, start_date: e.target.value })} dir="ltr" data-testid="range-start-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">إلى تاريخ</label>
            <Input type="date" value={rangeForm.end_date || ''} onChange={(e) => setRangeForm({ ...rangeForm, end_date: e.target.value })} dir="ltr" data-testid="range-end-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveRange} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-range-btn">
              <Save className="w-4 h-4 ml-1" /> {saving ? 'جارٍ الحفظ...' : 'حفظ'}
            </Button>
            <Button variant="outline" onClick={clearRange} disabled={saving} className="flex-1" data-testid="clear-range-btn">مسح</Button>
          </div>
        </div>
      </Card>

      {/* Admin preview */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-accent mb-4">معاينة نقاط قارئ (للإدارة)</h3>
        <p className="text-xs text-muted-foreground mb-4">
          هذه معاينة إدارية مستقلة عن النطاق أعلاه — تُظهر مجموع النقاط الكلية للقارئ + إمكانية فلترة حسب أي فترة تريد.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">رقم القارئ</label>
            <Input value={previewNum} onChange={(e) => setPreviewNum(e.target.value)} placeholder="13431" data-testid="preview-num-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">من</label>
            <Input type="date" value={previewStart} onChange={(e) => setPreviewStart(e.target.value)} dir="ltr" data-testid="preview-start-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">إلى</label>
            <Input type="date" value={previewEnd} onChange={(e) => setPreviewEnd(e.target.value)} dir="ltr" data-testid="preview-end-input" />
          </div>
          <Button onClick={runPreview} disabled={previewLoading} className="bg-primary hover:bg-primary/90" data-testid="preview-btn">
            <Search className="w-4 h-4 ml-1" /> {previewLoading ? 'جارٍ...' : 'معاينة'}
          </Button>
        </div>

        {previewData && (
          <div className="mt-6 space-y-3">
            <p className="font-semibold text-accent">
              {previewData.name} <span className="text-sm text-muted-foreground">({previewData.reader_number})</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="النقاط في النطاق" value={previewData.summary.points} icon={Trophy} color="text-amber-500" />
              <StatCard label="الصفحات" value={previewData.summary.total_pages} icon={TrendingUp} color="text-primary" />
              <StatCard label="الكتب المكتملة" value={previewData.summary.books_count} icon={BookOpen} color="text-emerald-600" />
              <StatCard label="عدد التقارير" value={previewData.summary.reports_count} icon={FileText} color="text-secondary" />
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};


// ============================================================================
// ACHIEVEMENTS TAB — Reader search with autocomplete + reports edit/delete
// ============================================================================
const AchievementsSection = () => {
  const [directory, setDirectory] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('all'); // 'all' | 'male' | 'female'
  const [selected, setSelected] = useState(null); // {reader_number, name, gender, label}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, report: null, form: {} });
  const [saving, setSaving] = useState(false);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/readers-directory');
        setDirectory(res.data);
      } catch { toast.error('فشل تحميل قائمة القراء'); }
      finally { setDirLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    let out = directory;
    if (genderFilter !== 'all') out = out.filter(x => x.gender === genderFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      out = out.filter(x => String(x.reader_number).toLowerCase().includes(q) || String(x.name).toLowerCase().includes(q));
    }
    return out.slice(0, 100);
  }, [directory, query, genderFilter]);

  const selectReader = async (r) => {
    setSelected(r);
    setQuery(`${r.name} (${r.reader_number})`);
    setShowList(false);
    setLoading(true);
    try {
      const res = await api.get(`/admin/points/${r.reader_number}`);
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل التحميل'); setData(null); }
    finally { setLoading(false); }
  };

  const reload = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/points/${selected.reader_number}`);
      setData(res.data);
    } catch { toast.error('فشل التحديث'); }
    finally { setLoading(false); }
  };

  const saveReport = async () => {
    setSaving(true);
    try {
      const payload = {
        title: editModal.form.title,
        classification: editModal.form.classification,
        pages_read: Number(editModal.form.pages_read) || 0,
        is_completed: editModal.form.is_completed,
        author: editModal.form.author || '',
      };
      await api.put(`/admin/reports/${editModal.report.id}`, payload);
      toast.success('تم تحديث التقرير');
      setEditModal({ open: false, report: null, form: {} });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل التحديث'); }
    finally { setSaving(false); }
  };

  const deleteReport = async (r) => {
    if (!window.confirm(`حذف تقرير "${r.title || r.classification}"؟`)) return;
    try {
      await api.delete(`/admin/reports/${r.id}`);
      toast.success('تم الحذف');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'فشل الحذف'); }
  };

  const exportCSV = async () => {
    if (!selected) return;
    try {
      const res = await api.get(`/admin/points/${selected.reader_number}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `points_${selected.reader_number}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم التصدير');
    } catch { toast.error('فشل التصدير'); }
  };

  return (
    <div className="space-y-6" data-testid="achievements-section">
      {/* Search + gender filter */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2 relative">
            <label className="block text-sm font-medium mb-1">بحث برقم القارئ أو الاسم</label>
            <div className="relative">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setShowList(true); }}
                onFocus={() => setShowList(true)}
                placeholder="ابدأ بالكتابة..."
                data-testid="search-input"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
            {showList && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border-2 rounded-xl shadow-lg max-h-80 overflow-y-auto" data-testid="search-results">
                {dirLoading ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">جارٍ التحميل...</p>
                ) : filtered.length === 0 ? (
                  <p className="p-4 text-center text-sm text-muted-foreground">لا نتائج</p>
                ) : (
                  <>
                    <div className="p-2 text-xs text-muted-foreground bg-muted/30 sticky top-0">
                      {filtered.length} نتيجة
                    </div>
                    {filtered.map((r) => (
                      <button
                        key={r.reader_number + r.gender}
                        onClick={() => selectReader(r)}
                        className="w-full text-right p-3 hover:bg-primary/10 border-b transition-colors flex items-center gap-3"
                        data-testid={`result-${r.reader_number}`}
                      >
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${r.gender === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                          {r.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-accent truncate">{r.name || '—'}</p>
                          <p className="text-xs text-muted-foreground" dir="ltr">{r.reader_number}</p>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">التصنيف</label>
            <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} className="w-full border-2 rounded-xl p-3 focus:outline-none focus:border-primary" data-testid="gender-filter">
              <option value="all">الكل ({directory.length})</option>
              <option value="male">قراء ({directory.filter(x => x.gender === 'male').length})</option>
              <option value="female">قارئات ({directory.filter(x => x.gender === 'female').length})</option>
            </select>
          </div>
        </div>
      </Card>

      {selected && data && (
        <>
          {/* Summary */}
          <div>
            <p className="mb-3 text-lg">
              <span className="font-bold text-accent">{data.name || '—'}</span>
              <span className="text-sm text-muted-foreground mr-2">({data.reader_number})</span>
              <span className={`mr-2 px-2 py-0.5 rounded-full text-xs font-semibold ${selected.gender === 'female' ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>
                {selected.label}
              </span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="النقاط الكلية" value={data.summary.points} icon={Trophy} color="text-amber-500" />
              <StatCard label="إجمالي الصفحات" value={data.summary.total_pages} icon={TrendingUp} color="text-primary" />
              <StatCard label="الكتب المنجزة" value={data.summary.books_count} icon={BookOpen} color="text-emerald-600" sub={`${data.summary.books_pages} صفحة`} />
              <StatCard label="الإنجازات" value={data.summary.total_achievements} icon={FileText} color="text-secondary" sub={`${data.summary.articles_count} مقالة · ${data.summary.studies_count} دراسة`} />
            </div>
          </div>

          {/* Reports Table */}
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b bg-muted/30 flex items-center justify-between">
              <h3 className="font-bold text-accent">التقارير ({data.summary.reports_count})</h3>
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                <Download className="w-4 h-4 ml-1" /> تصدير CSV
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div></div>
            ) : data.reports.length === 0 ? (
              <p className="p-8 text-center text-muted-foreground">لا توجد تقارير</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/20 text-xs">
                    <tr>
                      <th className="p-3 text-right">التاريخ</th>
                      <th className="p-3 text-right">التصنيف</th>
                      <th className="p-3 text-right">العنوان</th>
                      <th className="p-3 text-center">الصفحات</th>
                      <th className="p-3 text-center">مكتمل</th>
                      <th className="p-3 text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reports.map((r) => (
                      <tr key={r.id} className="border-t hover:bg-muted/10" data-testid={`report-${r.id}`}>
                        <td className="p-3 whitespace-nowrap text-xs">{r.date}</td>
                        <td className="p-3 whitespace-nowrap">{r.classification}</td>
                        <td className="p-3 max-w-xs truncate">{r.title || '—'}</td>
                        <td className="p-3 text-center">{r.pages_read}</td>
                        <td className="p-3 text-center">{r.is_completed === 'نعم' ? '✓' : '—'}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-center">
                            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setEditModal({ open: true, report: r, form: { ...r } })} data-testid={`edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="outline" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteReport(r)} data-testid={`delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {!selected && !dirLoading && (
        <Card className="p-16 text-center">
          <Users className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground">ابحث عن قارئ لعرض إنجازاته</p>
        </Card>
      )}

      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">تعديل تقرير</h2>
              <button onClick={() => setEditModal({ open: false, report: null, form: {} })}><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">العنوان</label>
                <Input value={editModal.form.title || ''} onChange={(e) => setEditModal({ ...editModal, form: { ...editModal.form, title: e.target.value } })} data-testid="edit-title" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">التصنيف</label>
                <Input value={editModal.form.classification || ''} onChange={(e) => setEditModal({ ...editModal, form: { ...editModal.form, classification: e.target.value } })} data-testid="edit-classification" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">عدد الصفحات</label>
                <Input type="number" value={editModal.form.pages_read ?? 0} onChange={(e) => setEditModal({ ...editModal, form: { ...editModal.form, pages_read: e.target.value } })} dir="ltr" data-testid="edit-pages" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">المؤلف</label>
                <Input value={editModal.form.author || ''} onChange={(e) => setEditModal({ ...editModal, form: { ...editModal.form, author: e.target.value } })} data-testid="edit-author" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">مكتمل</label>
                <select value={editModal.form.is_completed || 'لا'} onChange={(e) => setEditModal({ ...editModal, form: { ...editModal.form, is_completed: e.target.value } })} className="w-full border-2 rounded-xl p-3 focus:outline-none focus:border-primary" data-testid="edit-completed">
                  <option value="نعم">نعم</option>
                  <option value="لا">لا</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={saveReport} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-report-btn">
                {saving ? 'جارٍ الحفظ...' : 'حفظ'}
              </Button>
              <Button variant="outline" onClick={() => setEditModal({ open: false, report: null, form: {} })} className="flex-1">إلغاء</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-muted-foreground mb-1">{label}</p>
        <p className={`text-3xl font-bold ${color}`}>{Number(value || 0).toLocaleString('ar')}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <Icon className={`w-8 h-8 ${color} opacity-30`} />
    </div>
  </Card>
);

export default AdminPointsPage;
